import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { postService, queryService, meetingService } from '../services/api';
import Icon from '../components/Icon';
import PdfViewer from '../components/PdfViewer';
import { Watermark, useScreenGuard } from '../components/ProtectedView';

const TABS = [
  { key: 'note', label: 'Notes', single: 'Note', icon: 'note', kind: 'post' },
  { key: 'blog', label: 'Blogs', single: 'Blog', icon: 'blog', kind: 'post' },
  { key: 'update', label: 'Updates', single: 'Update', icon: 'update', kind: 'post' },
  { key: 'instruction', label: 'Instructions', single: 'Instruction', icon: 'instruction', kind: 'post' },
  { key: 'video', label: 'Videos', single: 'Video', icon: 'video', kind: 'post' },
  { key: 'queries', label: 'Queries', icon: 'query', kind: 'query' },
  { key: 'personal', label: 'Personal Sessions', icon: 'personal', kind: 'meeting' },
  { key: 'group', label: 'Group Meetings', icon: 'group', kind: 'meeting' },
];

const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

// Turns a YouTube link into an embed link; returns null for other links
const youtubeEmbed = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return match ? `https://www.youtube-nocookie.com/embed/${match[1]}?rel=0&modestbranding=1` : null;
};

const noMenu = (e) => e.preventDefault();

// Video player with the download button, picture-in-picture and right-click turned off
function VideoPlayer({ url }) {
  const embed = youtubeEmbed(url);
  return (
    <Watermark>
      <div className="hub-video">
        {embed
          ? <iframe src={embed} title="video" allow="encrypted-media; fullscreen" allowFullScreen />
          : <video src={url} controls controlsList="nodownload noplaybackrate noremoteplayback" disablePictureInPicture onContextMenu={noMenu} />}
      </div>
    </Watermark>
  );
}

const formatSize = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Shows an uploaded file inside the site. Students can only view, never download.
function FileView({ post }) {
  const type = post.fileType || '';
  const needsPass = type.startsWith('video/') || type.startsWith('audio/') || type.startsWith('image/');
  const [pass, setPass] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!needsPass) return;
    postService.fileToken(post._id).then(setPass).catch((e) => setError(e.message));
  }, [post._id, needsPass]);

  if (error) return <p className="hub-file-note">{error}</p>;

  if (type === 'application/pdf') return <Watermark><PdfViewer file={post.file} /></Watermark>;

  if (needsPass) {
    if (!pass) return <p className="hub-file-note">Loading...</p>;
    const src = postService.fileSrc(post.file, pass);
    if (type.startsWith('video/')) return <VideoPlayer url={src} />;
    if (type.startsWith('audio/')) {
      return (
        <Watermark>
          <audio className="hub-audio" src={src} controls controlsList="nodownload noplaybackrate" onContextMenu={noMenu} />
        </Watermark>
      );
    }
    return <Watermark><img className="hub-image" src={src} alt={post.title} draggable={false} /></Watermark>;
  }

  // Word, PowerPoint, ZIP...: only teachers and admins ever receive these
  return <StaffFile post={post} />;
}

function StaffFile({ post }) {
  const download = async () => {
    const pass = await postService.fileToken(post._id);
    window.location.href = postService.fileSrc(post.file, pass);
  };
  return (
    <button type="button" className="hub-file" onClick={download}>
      <span className="icon-badge"><Icon name="file" size={20} /></span>
      <span className="hub-file__info">
        <strong>{post.fileName}</strong>
        <small>{formatSize(post.fileSize)} · Teachers only. Students cannot see this file; upload a PDF for them.</small>
      </span>
      <span className="hub-file__action">Download</span>
    </button>
  );
}

function PostsTab({ type, single }) {
  const { user } = useAuth();
  const canPost = user.role === 'teacher' || user.role === 'admin';
  const isVideo = type === 'video';
  const [posts, setPosts] = useState([]);
  const emptyForm = { title: '', body: '', videoUrl: '', subject: '' };
  const [form, setForm] = useState(emptyForm);
  const [videoMode, setVideoMode] = useState('upload');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const load = () => postService.getAll(type).then(setPosts).catch((e) => setError(e.message));
  useEffect(() => { load(); setShowForm(false); setFile(null); }, [type]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const usingLink = isVideo && videoMode === 'link';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (isVideo && !usingLink && !file) return setError('Please choose a video file.');
      if (file && !usingLink) {
        setProgress(0);
        await postService.createWithFile({ type, title: form.title, body: form.body, subject: form.subject }, file, setProgress);
      } else {
        await postService.create({ ...form, type });
      }
      setForm(emptyForm);
      setFile(null);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setProgress(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await postService.delete(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      {error && <div className="error-msg">{error}</div>}

      {canPost && (
        <button className="btn-primary hub-add-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : <><Icon name="plus" size={16} /> Add {single}</>}
        </button>
      )}

      {showForm && (
        <form className="hub-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input value={form.title} onChange={set('title')} required />
          </div>
          <div className="form-group">
            <label>Subject (optional)</label>
            <input value={form.subject} onChange={set('subject')} placeholder="e.g. Chemistry" />
          </div>

          <div className="form-group">
            {isVideo && (
              <div className="hub-switch">
                <button type="button" className={videoMode === 'upload' ? 'active' : ''} onClick={() => setVideoMode('upload')}>
                  Upload video
                </button>
                <button type="button" className={videoMode === 'link' ? 'active' : ''} onClick={() => setVideoMode('link')}>
                  YouTube link
                </button>
              </div>
            )}
            {usingLink ? (
              <>
                <label>Video link (YouTube or direct .mp4 link)</label>
                <input value={form.videoUrl} onChange={set('videoUrl')} required />
              </>
            ) : (
              <>
                <label>{isVideo ? 'Video file' : 'Attach a file (optional)'}</label>
                <input type="file" accept={isVideo ? 'video/*' : undefined} onChange={(e) => setFile(e.target.files[0] || null)} />
                {file && <small className="hub-file-name">{file.name} · {formatSize(file.size)}</small>}
                {!isVideo && <small className="hub-file-name">Students can view PDF, images, audio and video inside the site (no download). Word, PowerPoint, Excel and ZIP files are visible to teachers only, so save them as PDF for students.</small>}
              </>
            )}
          </div>

          <div className="form-group">
            <label>{isVideo || file ? 'Description (optional)' : 'Content'}</label>
            <textarea rows={6} value={form.body} onChange={set('body')} required={!isVideo && !file} />
          </div>
          {progress !== null && (
            <div className="hub-progress">
              <div className="hub-progress__bar" style={{ width: `${progress}%` }} />
              <span>{progress < 100 ? `Uploading... ${progress}%` : 'Processing...'}</span>
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={progress !== null}>
            {progress !== null ? 'Uploading...' : 'Publish'}
          </button>
        </form>
      )}

      {posts.length === 0 ? (
        <div className="empty-state">Nothing here yet.</div>
      ) : (
        <div className="hub-list">
          {posts.map((p) => (
            <div key={p._id} className="hub-card">
              <div className="hub-card__head">
                <h3>{p.title}</h3>
                {canPost && (user.role === 'admin' || p.author?._id === user._id) && (
                  <button className="action-btn action-reject" onClick={() => handleDelete(p._id)}>Delete</button>
                )}
              </div>
              <div className="hub-card__meta">
                {p.subject && <span className="hub-tag">{p.subject}</span>}
                By {p.author?.name || 'MK Classes'} · {formatDate(p.createdAt)}
              </div>
              {p.file && <FileView post={p} />}
              {!p.file && p.videoUrl && <VideoPlayer url={p.videoUrl} />}
              {p.body && <p className="hub-card__body">{p.body}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QueriesTab() {
  const { user } = useAuth();
  const isStudent = user.role === 'student';
  const [queries, setQueries] = useState([]);
  const [form, setForm] = useState({ question: '', subject: '' });
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');

  const load = () => queryService.getAll().then(setQueries).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const handleAsk = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await queryService.create(form);
      setForm({ question: '', subject: '' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAnswer = async (id) => {
    if (!answers[id]) return;
    try {
      await queryService.answer(id, answers[id]);
      setAnswers({ ...answers, [id]: '' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      {error && <div className="error-msg">{error}</div>}

      {isStudent && (
        <form className="hub-form" onSubmit={handleAsk}>
          <div className="form-group">
            <label>Ask your doubt</label>
            <textarea
              rows={3}
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Type your question here..."
              required
            />
          </div>
          <div className="form-group">
            <label>Subject (optional)</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </div>
          <button type="submit" className="btn-primary">Submit Query</button>
        </form>
      )}

      {queries.length === 0 ? (
        <div className="empty-state">No queries yet.</div>
      ) : (
        <div className="hub-list">
          {queries.map((q) => (
            <div key={q._id} className="hub-card">
              <div className="hub-card__head">
                <h3>{q.question}</h3>
                <span className={`status-badge ${q.status === 'answered' ? 'status-completed' : 'status-pending'}`}>
                  {q.status}
                </span>
              </div>
              <div className="hub-card__meta">
                {q.subject && <span className="hub-tag">{q.subject}</span>}
                {!isStudent && `Asked by ${q.student?.name} · `}{formatDate(q.createdAt)}
              </div>
              {q.answer ? (
                <p className="hub-answer"><strong>Answer ({q.answeredBy?.name}):</strong> {q.answer}</p>
              ) : !isStudent && (
                <div className="hub-answer-form">
                  <textarea
                    rows={2}
                    placeholder="Write your answer..."
                    value={answers[q._id] || ''}
                    onChange={(e) => setAnswers({ ...answers, [q._id]: e.target.value })}
                  />
                  <button className="btn-primary" onClick={() => handleAnswer(q._id)}>Answer</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MeetingsTab({ type }) {
  const { user } = useAuth();
  const isStudent = user.role === 'student';
  const canCreate = user.role === 'teacher' || user.role === 'admin';
  const [meetings, setMeetings] = useState([]);
  const emptyForm = { title: '', description: '', date: '', timeSlot: '', meetingLink: '', maxStudents: 20 };
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const load = () => meetingService.getAll(type).then(setMeetings).catch((e) => setError(e.message));
  useEffect(() => { load(); setShowForm(false); }, [type]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await meetingService.create({ ...form, type });
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleJoin = async (id) => {
    setError('');
    try {
      await meetingService.join(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await meetingService.delete(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      {error && <div className="error-msg">{error}</div>}

      {canCreate && (
        <button className="btn-primary hub-add-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : <><Icon name="plus" size={16} /> {type === 'personal' ? 'Add Personal Session' : 'Add Group Meeting'}</>}
        </button>
      )}

      {showForm && (
        <form className="hub-form" onSubmit={handleCreate}>
          <div className="form-group">
            <label>Title</label>
            <input value={form.title} onChange={set('title')} required />
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <textarea rows={2} value={form.description} onChange={set('description')} />
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
              <label>Date</label>
              <input type="date" value={form.date} onChange={set('date')} required />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: '140px' }}>
              <label>Time</label>
              <input placeholder="e.g. 5:00 PM - 6:00 PM" value={form.timeSlot} onChange={set('timeSlot')} required />
            </div>
            {type === 'group' && (
              <div className="form-group" style={{ flex: 1, minWidth: '120px' }}>
                <label>Max students</label>
                <input type="number" min="2" value={form.maxStudents} onChange={set('maxStudents')} />
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Meeting link (Google Meet / Zoom)</label>
            <input value={form.meetingLink} onChange={set('meetingLink')} placeholder="https://meet.google.com/..." />
          </div>
          <button type="submit" className="btn-primary">Create</button>
        </form>
      )}

      {meetings.length === 0 ? (
        <div className="empty-state">No sessions scheduled.</div>
      ) : (
        <div className="hub-list">
          {meetings.map((m) => (
            <div key={m._id} className="hub-card">
              <div className="hub-card__head">
                <h3>{m.title}</h3>
                {canCreate && (user.role === 'admin' || m.teacher?._id === user._id) && (
                  <button className="action-btn action-reject" onClick={() => handleDelete(m._id)}>Delete</button>
                )}
              </div>
              <div className="hub-card__meta hub-meta-row">
                <span><Icon name="calendar" size={14} /> {formatDate(m.date)}</span>
                <span><Icon name="clock" size={14} /> {m.timeSlot}</span>
                <span><Icon name="personal" size={14} /> {m.teacher?.name}</span>
                {type === 'group' && <span><Icon name="group" size={14} /> {m.seatsLeft} seats left</span>}
              </div>
              {m.description && <p className="hub-card__body">{m.description}</p>}

              {!isStudent && m.students?.length > 0 && (
                <p className="hub-card__meta">Students: {m.students.map((s) => s.name).join(', ')}</p>
              )}

              {m.meetingLink && (
                <a href={m.meetingLink} target="_blank" rel="noreferrer" className="btn-primary hub-join-link">
                  <Icon name="link" size={16} /> Open Meeting Link
                </a>
              )}
              {isStudent && !m.joined && (
                m.seatsLeft > 0
                  ? <button className="btn-primary" onClick={() => handleJoin(m._id)}>{type === 'personal' ? 'Book' : 'Join'}</button>
                  : <span className="status-badge status-cancelled">Full</span>
              )}
              {isStudent && m.joined && !m.meetingLink && (
                <span className="status-badge status-accepted">Joined. The link will appear here</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StudyHub() {
  const { user } = useAuth();
  const [active, setActive] = useState(TABS[0]);
  const isStudent = user.role === 'student';
  const hidden = useScreenGuard(isStudent);

  return (
    <div className={`hub-page${isStudent ? ' hub-page--student' : ''}${hidden ? ' hub-page--hidden' : ''}`}>
      {hidden && <div className="hub-shield">Content hidden. Click here to continue reading.</div>}
      <h1 className="section-title">Study Hub</h1>
      <p className="hub-subtitle">Notes, blogs, updates, videos, doubts and live classes, all in one place.</p>

      <div className="tab-bar hub-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn ${active.key === t.key ? 'active' : ''}`}
            onClick={() => setActive(t)}
          >
            <Icon name={t.icon} size={16} /> {t.label}
          </button>
        ))}
      </div>

      {active.kind === 'post' && <PostsTab type={active.key} single={active.single} />}
      {active.kind === 'query' && <QueriesTab />}
      {active.kind === 'meeting' && <MeetingsTab type={active.key} />}
    </div>
  );
}
